/* eslint-disable no-console */
/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React, { Suspense, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import injectSaga from 'utils/injectSaga';
import Modal from 'react-modal';

import { LastLocationProvider } from 'react-router-last-location';

import LoadingScreen from '../../components/LoadingIndicator';
import ScrollToTop from '../../components/ScrollToTop';
import { captureLandingContext } from '../../utils/segmentGa4Context';
import { captureGoogleClickIdFromUrl } from '../../utils/segmentGoogleAds';
import BlogPage from '../Blog';
import EligibleRoute from '../App/EligibleRoute';
import PrivateRoute from '../App/PrivateRoute';
import AppointmentSaga from '../Appointment/saga';
import { logoutUser } from '../User/actions';
import Jobs from '../../components/Jobs';
import lazyLoad from '../../utils/react-lazy-load';

const FranchisePhase2 = lazyLoad(() =>
  import('../Appointment/franchise_phases/phase2'),
);
const HomePage = lazyLoad(() => import('../Home'));
const Appointment = lazyLoad(() => import('../Appointment'));
const MyAppointments = lazyLoad(() => import('../Appointment/appointments'));
const { InviteFriendsShare } = lazyLoad(() => import('../InviteFriends/share'));
const MyTherapists = lazyLoad(() => import('../Therapist/my_therapists'));
const OwnSqueeze = lazyLoad(() => import('../../components/OwnSqueeze'));
const Abandonmentpage = lazyLoad(() => import('../Abandonmentpage'));
const ErrorContent = lazyLoad(() => import('../../components/ErrorContent'));
// const Philanthropy = lazyLoad(() => import('../../components/Philanthropy'));
const DesktopInstall = lazyLoad(() =>
  import('../../components/DesktopInstall'),
);
const TermsOfUse = lazyLoad(() => import('../../components/TermsOfUse'));
const PrivacyPolicy = lazyLoad(() => import('../../components/PrivacyPolicy'));
const ChangePassword = lazyLoad(() => import('../ChangePassword'));
const ForgotPassword = lazyLoad(() => import('../ForgotPassword'));
const ForgotPasswordLinkSent = lazyLoad(() =>
  import('../ForgotPassword/password-reset-link-sent'),
);
const ResetPassword = lazyLoad(() =>
  import('../ForgotPassword/reset-password'),
);
const Therapist = lazyLoad(() => import('../Therapist'));
const HowItWorks = lazyLoad(() => import('../../components/HowItWorks'));
const Gift = lazyLoad(() => import('../Gift'));
const Package = lazyLoad(() => import('../Package'));
const Preferences = lazyLoad(() => import('../Preferences'));
const Membership = lazyLoad(() => import('../Membership'));
const Signup = lazyLoad(() => import('../Signup'));
const Login = lazyLoad(() => import('../Login'));
const Pricing = lazyLoad(() => import('../Pricing'));
const Print = lazyLoad(() => import('../Print'));
// const AboutUs = lazyLoad(() => import('../../components/AboutUs'));
const Careers = lazyLoad(() => import('../../components/Careers'));
const Profile = lazyLoad(() => import('../Profile'));
const InviteFriends = lazyLoad(() => import('../InviteFriends'));
const Gratuity = lazyLoad(() => import('../Gratuity-Rating-Review'));
const Blog = lazyLoad(() => import('../Blog'));

// Extract slug from pathname /blog/foo -> foo; used so blog always matches under /blog
function getBlogSlug(pathname) {
  if (!pathname || !pathname.startsWith('/blog/')) return null;
  const slug = pathname.replace(/^\/blog\/?/, '').split('/')[0];
  return slug || null;
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    padding: '0px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '450px',
  },
};

const App = props => {
  const { location, history } = props;
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [blogSlugFromUrl, setBlogSlugFromUrl] = useState(null);

  useEffect(() => {
    const handleInvalidToken = e => {
      if (e.key === 'userToken' && e.oldValue && !e.newValue) {
        setIsLoggedOut(true);
      }
    };
    window.addEventListener('storage', handleInvalidToken);
  }, []);

  useEffect(() => {
    // Add Zendesk widget script to all pages
    if (!document.getElementById('ze-snippet')) {
      const script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src =
        'https://static.zdassets.com/ekr/snippet.js?key=64682ef2-475e-4cb2-ac25-17bf70fcedb4';
      document.body.appendChild(script);
    }
  }, []);

  useEffect(
    () => {
      if (typeof window === 'undefined') return;
      const path = window.location.pathname;
      const slug = getBlogSlug(path);
      setBlogSlugFromUrl(path.startsWith('/blog/') ? slug || null : null);
    },
    [location && location.pathname],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    const slug = getBlogSlug(path);
    if (path.startsWith('/blog/') && slug) setBlogSlugFromUrl(slug);
  }, []);

  // Capture landing referrer/url and gclid/UTM from URL for Segment → GA4 attribution (avoid "Unassigned")
  useEffect(
    () => {
      if (typeof window === 'undefined') return;
      captureLandingContext();
      captureGoogleClickIdFromUrl();
    },
    [location && location.search],
  );

  // Handle /blog/:slug before Switch; only show blog when URL is /blog/... (so navigating away works)
  const pathname =
    (typeof window !== 'undefined' && window.location.pathname) ||
    (location && location.pathname) ||
    '';
  const slugFromPath = pathname.startsWith('/blog/')
    ? getBlogSlug(pathname)
    : null;
  const blogSlug =
    slugFromPath ||
    (blogSlugFromUrl && (pathname.startsWith('/blog/') || !pathname)
      ? blogSlugFromUrl
      : null);
  if (blogSlug) {
    return (
      <BlogPage
        match={{
          params: { slug: blogSlug },
          path: '/blog',
          url: `/blog/${blogSlug}`,
          isExact: pathname === `/blog/${blogSlug}`,
        }}
        location={
          location ||
          (typeof window !== 'undefined' && {
            pathname: window.location.pathname,
          }) ||
          {}
        }
        history={history}
      />
    );
  }

  return (
    <LastLocationProvider>
      <ScrollToTop>
        <Modal isOpen={isLoggedOut} style={customStyles}>
          <div className="NotifyTherapistModal" style={{ textAlign: 'center' }}>
            <button
              type="button"
              className="modal__close btn-icon icon js-modal-close"
              onClick={() => {
                logoutUser();
                window.location.reload();
              }}
            >
              <span className="screenreader">Close</span>
            </button>
            <div className="bubble">
              <p className="NotifyTherapistModal-bubble-text">
                You were logged out!
              </p>
            </div>
            <div className="preg-modal__smiley">
              <i className="icon icon--smiley-blue-reflection" />
            </div>
          </div>
        </Modal>
        <Suspense fallback={<LoadingScreen hangTight />}>
          <Switch>
            <PrivateRoute
              path="/gratuity"
              toPath="/login"
              component={Gratuity}
            />
            <Route path="/appointment" component={Appointment} />
            <Route path="/therapist" component={Therapist} />
            <Route path="/membership" component={Membership} />
            <Route path="/gift" component={Gift} />
            <Route path="/packages" component={Package} />
            <Route path="/package" component={Package} />
            <Route path="/register" component={Signup} />
            <Route path="/login" component={Login} />
            <Route path="/print/:giftId" component={Print} />
            <PrivateRoute
              path="/preferences"
              toPath="/login"
              // eligibleOnly
              component={Preferences}
            />
            <PrivateRoute
              path="/appointments"
              toPath="/login"
              eligibleOnly
              component={MyAppointments}
            />
            <PrivateRoute
              path="/profile"
              toPath="/login"
              eligibleOnly
              component={Profile}
            />
            <PrivateRoute
              path="/my-therapists"
              toPath="/login"
              eligibleOnly
              component={MyTherapists}
            />
            <Redirect from="/thank-you" to="/#thank-you" />
            <EligibleRoute exact path="/" component={HomePage} />
            <EligibleRoute
              path="/abandonmentpage"
              component={Abandonmentpage}
            />
            <EligibleRoute exact path="/pricing" component={Pricing} />
            <Redirect from="/about-us" to="/" />
            {/* <EligibleRoute path="/about-us" component={AboutUs} /> */}
            {/* <EligibleRoute path="/franchising" component={OwnSqueeze} /> */}
            <EligibleRoute path="/happy" component={OwnSqueeze} />
            <Redirect from="/earn" to="/" />
            <EligibleRoute path="/careers" component={Careers} />
            <EligibleRoute path="/jobs" component={Jobs} />
            <EligibleRoute path="/how-it-works" component={HowItWorks} />
            <EligibleRoute path="/privacy-policy" component={PrivacyPolicy} />
            <EligibleRoute path="/terms-of-use" component={TermsOfUse} />
            <Redirect from="/philanthropy" to="/" />
            {/* <EligibleRoute path="/philanthropy" component={Philanthropy} /> */}
            <Redirect from="/joinus" to="/careers" />
            <EligibleRoute
              path="/invite/share"
              component={InviteFriendsShare}
            />
            <PrivateRoute
              path="/invite"
              toPath="/login" // eligibleOnly
              component={InviteFriends}
            />
            <EligibleRoute path="/install" component={DesktopInstall} />

            <EligibleRoute
              exact
              path="/forgot-password"
              component={ForgotPassword}
            />
            <EligibleRoute
              exact
              path="/forgot-password/sent"
              component={ForgotPasswordLinkSent}
            />
            <EligibleRoute path="/password/reset" component={ResetPassword} />
            <EligibleRoute
              exact
              path="/change-password"
              component={ChangePassword}
            />
            <Route
              path="/*sweeps"
              render={routeProps => {
                const sweepsPathname =
                  typeof window !== 'undefined'
                    ? window.location.pathname
                    : routeProps.location.pathname;
                const slug = getBlogSlug(sweepsPathname);
                if (slug) {
                  return (
                    <Blog
                      match={{
                        params: { slug },
                        path: '/blog',
                        url: `/blog/${slug}`,
                        isExact: sweepsPathname === `/blog/${slug}`,
                      }}
                      location={routeProps.location}
                      history={routeProps.history}
                    />
                  );
                }
                return <FranchisePhase2 {...routeProps} />;
              }}
            />
            <Route
              path="*"
              render={routeProps => {
                const fallbackPathname =
                  typeof window !== 'undefined'
                    ? window.location.pathname
                    : routeProps.location.pathname;
                const slug = getBlogSlug(fallbackPathname);
                if (slug) {
                  return (
                    <Blog
                      match={{
                        params: { slug },
                        path: '/blog',
                        url: `/blog/${slug}`,
                        isExact: fallbackPathname === `/blog/${slug}`,
                      }}
                      location={routeProps.location}
                      history={routeProps.history}
                    />
                  );
                }
                return <ErrorContent />;
              }}
            />
          </Switch>
        </Suspense>
      </ScrollToTop>
    </LastLocationProvider>
  );
};

App.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

const withSagaCheckGiftCode = injectSaga({
  key: 'checkGiftCode',
  saga: AppointmentSaga.watchCheckGiftCode,
});
export default compose(
  withRouter,
  withSagaCheckGiftCode,
)(App);
